import { Link } from 'react-router-dom';

/**
 * Impressum (AP-B6) — § 5 TMG, § 18 MStV.
 *
 * Editorial-Stil konsistent mit Datenschutz-Seite. Pflichtangaben:
 *   - Anbieter (Firma, Vertretung, Anschrift, Kontakt)
 *   - Eintrag im Handelsregister + USt-ID
 *   - Berufsbezeichnung + Aufsichtsbehörde (AVGS-Träger nach § 45 SGB III)
 *   - Verantwortlich für Inhalt nach § 18 Abs. 2 MStV
 *   - Hinweis auf EU-Streitbeilegungsplattform (OS-Plattform)
 *
 * Texte wie bei Datenschutz: nicht in i18n, weil rechtlich verbindlich.
 */
export function Impressum() {
  return (
    <article className="pb-24 pt-12 md:pt-20">
      <header className="relative pb-10">
        <div
          aria-hidden="true"
          className="absolute right-0 top-0 hidden h-24 w-24 rounded-sm md:block"
          style={{ backgroundColor: 'var(--color-dim-activity)', opacity: 0.32 }}
        />
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          Information
        </p>
        <h1 className="mt-3 font-serif text-4xl font-black leading-none tracking-tight text-stone-900 md:text-6xl">
          IMPRESSUM.
        </h1>
        <p className="mt-3 max-w-prose font-serif text-base italic text-stone-500 md:text-lg">
          Anbieterkennzeichnung nach § 5 TMG.
        </p>
      </header>

      <div className="prose prose-stone max-w-prose font-serif text-base leading-relaxed text-stone-700">
        <Section title="Anbieter">
          <Address>
            Petschwork Consulting GmbH
            <br />
            Pettenkoferstr. 4
            <br />
            80336 München
            <br />
            Deutschland
          </Address>
          <p>
            Vertreten durch: <strong>Dr. Stefanie Petsch-van Randenborgh</strong>
          </p>
        </Section>

        <Section title="Kontakt">
          <p>
            Telefon: 089/54884586-0
            <br />
            E-Mail:{' '}
            <a href="mailto:kontakt@petschwork.de" className="underline">
              kontakt@petschwork.de
            </a>
          </p>
        </Section>

        <Section title="Registereintrag">
          <p>
            Eingetragen im Handelsregister des Amtsgerichts München
            <br />
            Registernummer: HRB 000000
          </p>
        </Section>

        <Section title="Umsatzsteuer-Identifikationsnummer">
          <p>
            USt-IdNr. nach § 27a UStG: <strong>DE000000000</strong>
          </p>
          <p className="text-sm italic text-stone-500">
            Aktualisierung sobald die Vergabe durch das BZSt vorliegt.
          </p>
        </Section>

        <Section title="Berufsbezeichnung und Aufsicht">
          <p>
            Berufsbezeichnung: <strong>AVGS-Träger nach § 45 SGB III</strong>
            <br />
            Verliehen in: Bundesrepublik Deutschland
          </p>
          <p>
            Zuständige Aufsicht: zuständige Bundesagentur für Arbeit, Regionaldirektion Bayern,
            Regensburger Straße 100, 90478 Nürnberg.
          </p>
          <p>
            Trägerzulassung nach AZAV: Erfolgt durch eine zugelassene fachkundige Stelle gemäß § 178
            SGB III. Der aktuelle Zulassungsbescheid wird auf Anfrage vorgelegt.
          </p>
        </Section>

        <Section title="Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV">
          <p>
            Christian Kopsch
            <br />
            Petschwork Consulting GmbH
            <br />
            Pettenkoferstr. 4
            <br />
            80336 München
          </p>
        </Section>

        <Section title="Hinweis zur Streitbeilegung">
          <p>
            Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{' '}
            <a
              href="https://ec.europa.eu/consumers/odr/"
              rel="noopener noreferrer"
              className="underline"
            >
              https://ec.europa.eu/consumers/odr/
            </a>
            .
          </p>
          <p>
            Wir sind nicht bereit oder verpflichtet, an einem Streitbeilegungsverfahren vor einer
            Verbraucherschlichtungsstelle teilzunehmen (§ 36 VSBG).
          </p>
        </Section>

        <Section title="Haftung für Inhalte">
          <p>
            Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten
            nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als
            Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde
            Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechts­widrige
            Tätigkeit hinweisen.
          </p>
        </Section>

        <Section title="Haftung für Links">
          <p>
            Unsere Anwendung enthält keine Links auf externe Webseiten Dritter. Falls in Zukunft
            Links eingebunden werden, übernehmen wir keine Gewähr für deren Inhalte; verantwortlich
            ist stets der jeweilige Anbieter der verlinkten Seite.
          </p>
        </Section>

        <Section title="Urheberrecht">
          <p>
            Die durch die Seitenbetreiber erstellten Inhalte und Werke unterliegen dem deutschen
            Urheberrecht. Vervielfältigung, Bearbeitung, Verbreitung oder jede Art der Verwertung
            außerhalb der Grenzen des Urheberrechts bedürfen der schriftlichen Zustimmung der
            Petschwork Consulting GmbH.
          </p>
        </Section>

        <p className="mt-12 border-t border-stone-200 pt-6 font-sans text-xs uppercase tracking-widest text-stone-500">
          Siehe auch:{' '}
          <Link to="/datenschutz" className="underline hover:text-stone-700">
            Datenschutz
          </Link>
        </p>
      </div>
    </article>
  );
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <section className="mt-10 first:mt-0">
      <h2 className="mb-3 font-serif text-xl font-bold tracking-tight text-stone-900">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Address({ children }: { children: React.ReactNode }) {
  return (
    <address className="not-italic rounded-sm border-l-2 border-stone-200 pl-4 font-serif text-base text-stone-700">
      {children}
    </address>
  );
}
